// Category metadata, navigation config, luxury imagery
import {
  LayoutDashboard, Building2, Home, Warehouse, Factory, Store, UtensilsCrossed,
  Gavel, FileWarning, Landmark, Scale, PackageX, Archive, MapPin, Users,
  Bookmark, FileText, BarChart3, Settings, Shield, DoorClosed, Sparkles,
  Search, TrendingDown, HardHat, Bell, Database, ClipboardList, Briefcase, ShoppingBag,
} from "lucide-react";

export const CATEGORY_META = {
  closing: { label: "Business Closures", color: "#f87171", icon: DoorClosed },
  liquidation: { label: "Liquidations", color: "#fb923c", icon: PackageX },
  foreclosure: { label: "Foreclosures", color: "#60a5fa", icon: FileWarning },
  tax_delinquent: { label: "Tax Delinquent", color: "#c084fc", icon: Landmark },
  vacant: { label: "Vacant Properties", color: "#94a3b8", icon: Archive },
  sheriff_sale: { label: "Sheriff Sales", color: "#f472b6", icon: Gavel },
  bankruptcy: { label: "Bankruptcies", color: "#fbbf24", icon: Scale },
  auction: { label: "Equipment Auctions", color: "#a78bfa", icon: Gavel },
  warehouse: { label: "Warehouse Closures", color: "#38bdf8", icon: Warehouse },
  manufacturing: { label: "Manufacturing", color: "#fb7185", icon: Factory },
  retail: { label: "Retail Closures", color: "#f472b6", icon: Store },
  restaurant: { label: "Restaurant Closures", color: "#facc15", icon: UtensilsCrossed },
  high_opportunity: { label: "High Opportunity", color: "#34d399", icon: Sparkles },
};

export const NAV_SECTIONS = [
  {
    label: "Intelligence",
    items: [
      { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
      { to: "/app/ai-search", label: "AI Search", icon: Sparkles, testid: "nav-ai-search" },
      { to: "/app/attom-lookup", label: "Property Lookup", icon: Search, testid: "nav-attom-lookup" },
      { to: "/app/investments", label: "Investment Insights", icon: TrendingDown, testid: "nav-investments" },
      { to: "/app/maps", label: "Interactive Map", icon: MapPin, testid: "nav-maps" },
      { to: "/app/analytics", label: "Analytics", icon: BarChart3, testid: "nav-analytics" },
    ],
  },
  {
    label: "Businesses",
    items: [
      { to: "/app/opportunities/closing", label: "Business Closures", icon: DoorClosed, testid: "nav-closing" },
      { to: "/app/opportunities/liquidation", label: "Liquidations", icon: PackageX, testid: "nav-liquidation" },
      { to: "/app/opportunities/bankruptcy", label: "Bankruptcies", icon: Scale, testid: "nav-bankruptcy" },
      { to: "/app/opportunities/warehouse", label: "Warehouse Closures", icon: Warehouse, testid: "nav-warehouse" },
      { to: "/app/opportunities/manufacturing", label: "Manufacturing", icon: Factory, testid: "nav-manufacturing" },
      { to: "/app/opportunities/retail", label: "Retail", icon: Store, testid: "nav-retail" },
      { to: "/app/opportunities/restaurant", label: "Restaurants", icon: UtensilsCrossed, testid: "nav-restaurant" },
    ],
  },
  {
    label: "Properties",
    items: [
      { to: "/app/opportunities/foreclosure", label: "Foreclosures", icon: FileWarning, testid: "nav-foreclosure" },
      { to: "/app/opportunities/tax_delinquent", label: "Tax Delinquencies", icon: Landmark, testid: "nav-tax" },
      { to: "/app/opportunities/sheriff_sale", label: "Sheriff Sales", icon: Gavel, testid: "nav-sheriff" },
      { to: "/app/opportunities/vacant", label: "Vacant Properties", icon: Archive, testid: "nav-vacant" },
      { to: "/app/opportunities/auction", label: "Auctions", icon: Gavel, testid: "nav-auction" },
    ],
  },
  {
    label: "Marketplace",
    items: [
      { to: "/app/marketplace", label: "Equipment Marketplace", icon: ShoppingBag, testid: "nav-marketplace" },
      { to: "/app/broker", label: "Broker Portal", icon: Briefcase, testid: "nav-broker" },
    ],
  },
  {
    label: "Workspace",
    items: [
      { to: "/app/crm", label: "Lead CRM", icon: Users, testid: "nav-crm" },
      { to: "/app/wholesale", label: "Wholesaler Suite", icon: FileText, testid: "nav-wholesale" },
      { to: "/app/saved-searches", label: "Saved Searches", icon: Bookmark, testid: "nav-saved" },
      { to: "/app/reports", label: "Reports", icon: FileText, testid: "nav-reports" },
      { to: "/app/referrals", label: "Refer & Earn", icon: Sparkles, testid: "nav-referrals" },
      { to: "/app/settings", label: "Settings", icon: Settings, testid: "nav-settings" },
    ],
  },
  {
    label: "Admin",
    items: [
      { to: "/app/admin", label: "Overview", icon: Shield, testid: "nav-admin", adminOnly: true },
      { to: "/app/admin/data-sources", label: "Data Sources", icon: Database, testid: "nav-datasources", adminOnly: true },
      { to: "/app/admin/audit-logs", label: "Audit Logs", icon: ClipboardList, testid: "nav-audit", adminOnly: true },
    ],
  },
];

export const HERO_IMAGE = "https://customer-assets.emergentagent.com/job_12faeb5b-d40e-4c8d-a2b3-d7791a1f3212/artifacts/r7f484k1_image.png";

export const LUXURY_IMAGES = [
  "https://images.unsplash.com/photo-1613977257363-707ba9348227?w=1600&q=85",
  "https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1600&q=85",
  "https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=1600&q=85",
  "https://images.unsplash.com/photo-1580587771525-78b9dba3b914?w=1600&q=85",
];

export const INDUSTRIES = [
  "CRE Brokerage", "Residential Realty", "Private Equity", "Investors",
  "Warehouse Development", "Business Brokers", "Banks & Lenders", "Liquidators",
  "Equipment Dealers", "Contractors", "Developers", "Property Management",
];

export const currency = (n) => {
  if (n == null) return "—";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

export const num = (n) => {
  if (n == null) return "—";
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toLocaleString();
};
